/** Bookings saved on this device (access by token, no account). */
import { useEffect, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { Badge } from '@astryxdesign/core/Badge';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Heading } from '@astryxdesign/core/Heading';
import { Icon } from '@astryxdesign/core/Icon';
import { HStack, VStack } from '@astryxdesign/core/Layout';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { Text } from '@astryxdesign/core/Text';
import { useToast } from '@astryxdesign/core/Toast';
import { CalendarBlank, LinkSimple, Phone } from '@phosphor-icons/react';
import { ApiError } from '@shared/errors.ts';
import type { BookingView } from '@shared/contracts.ts';
import { useTenant } from '@/app/tenant-context.ts';
import { errorText, publicApi } from '@/lib/api.ts';
import { dateTime, longDateTime, money } from '@/lib/format.ts';
import { forgetBooking, savedBookings, saveBooking, updateSavedBooking, type SavedBooking } from '@/lib/storage.ts';
import { ListSkeleton, ErrorView } from '@/components/StateViews.tsx';
import { useOpenBooking } from '@/features/booking/nav.ts';
import { ReminderControls } from './ReminderControls.tsx';
import { RescheduleSheet } from './RescheduleSheet.tsx';
import { useIndustry } from '@/lib/industry.ts';

const STATUS: Record<BookingView['status'], { label: string; variant: 'success' | 'info' | 'neutral' | 'warning' | 'error' }> = {
  confirmed: { label: 'Подтверждена', variant: 'success' },
  checked_in: { label: 'Машина в работе', variant: 'info' },
  completed: { label: 'Готово', variant: 'neutral' },
  cancelled: { label: 'Отменена', variant: 'error' },
  no_show: { label: 'Неявка', variant: 'warning' },
};

/** A shared link carries the token in the URL fragment (never sent to servers). */
function useImportFromFragment(slug: string, onImported: () => void) {
  useEffect(() => {
    const m = /[#&]t=(bk1_[A-Za-z0-9_-]{43})/.exec(location.hash);
    if (!m) return;
    const token = m[1]!;
    history.replaceState(history.state, '', location.pathname + location.search);
    publicApi
      .booking(slug, token)
      .then((b) => {
        saveBooking(slug, { id: b.id, token, startsAt: b.starts_at, serviceName: b.service_name });
        onImported();
      })
      .catch(() => {});
  }, [slug, onImported]);
}

export function MyBookingsPage() {
  const { slug } = useTenant();
  const openBooking = useOpenBooking();
  const [saved, setSaved] = useState<SavedBooking[]>(() => savedBookings(slug));
  const reload = () => setSaved(savedBookings(slug));
  useImportFromFragment(slug, reload);

  const results = useQueries({
    queries: saved.map((s) => ({
      queryKey: ['booking', slug, s.id],
      queryFn: ({ signal }: { signal: AbortSignal }) => publicApi.booking(slug, s.token, signal),
      retry: (n: number, e: unknown) => !(e instanceof ApiError && e.code === 'not_found') && n < 2,
    })),
  });

  // Tokens that no longer resolve (booking deleted, e.g. demo cleanup) are forgotten.
  useEffect(() => {
    results.forEach((r, i) => {
      if (r.error instanceof ApiError && r.error.code === 'not_found' && saved[i]) {
        forgetBooking(slug, saved[i]!.id);
      }
    });
  }, [results, saved, slug]);

  const items = saved
    .map((s, i) => ({ saved: s, q: results[i]! }))
    .filter((x) => !(x.q.error instanceof ApiError && x.q.error.code === 'not_found'))
    .sort((a, b) => (a.q.data?.starts_at ?? a.saved.startsAt).localeCompare(b.q.data?.starts_at ?? b.saved.startsAt));
  const upcoming = items.filter((x) => !x.q.data || ['confirmed', 'checked_in'].includes(x.q.data.status));
  const past = items.filter((x) => x.q.data && !['confirmed', 'checked_in'].includes(x.q.data.status));

  return (
    <VStack gap={5} padding={4}>
      <VStack gap={1} paddingBlockStart={4}>
        <Heading level={1}>Моя запись</Heading>
        <Text type="supporting">Записи, сделанные с этого устройства.</Text>
      </VStack>
      {items.length === 0 ? (
        <EmptyState
          title="На этом устройстве записей нет"
          description="Запишитесь онлайн — запись появится здесь. Если записывались с другого телефона, откройте ссылку на запись оттуда."
          icon={<Icon icon={CalendarBlank} size="lg" />}
          actions={<Button label="Записаться" variant="primary" onClick={() => openBooking()} />}
        />
      ) : (
        <>
          {upcoming.map((x) => (
            <BookingCard key={x.saved.id} saved={x.saved} q={x.q} />
          ))}
          {past.length > 0 && (
            <VStack gap={3}>
              <Heading level={2}>Прошедшие и отменённые</Heading>
              {past.map((x) => (
                <BookingCard key={x.saved.id} saved={x.saved} q={x.q} />
              ))}
            </VStack>
          )}
        </>
      )}
    </VStack>
  );
}

function BookingCard({ saved, q }: { saved: SavedBooking; q: { data?: BookingView; error: unknown; isPending: boolean; refetch: () => unknown } }) {
  const { slug, tenant } = useTenant();
  const copy = useIndustry();
  const qc = useQueryClient();
  const showToast = useToast();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [reschedule, setReschedule] = useState(false);

  if (q.isPending) return <ListSkeleton rows={2} />;
  if (!q.data) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const b = q.data;
  const st = STATUS[b.status];

  const cancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await publicApi.cancel(slug, saved.token, crypto.randomUUID());
      setConfirmCancel(false);
      showToast({ body: 'Запись отменена' });
      await qc.invalidateQueries({ queryKey: ['booking', slug, b.id] });
      void qc.invalidateQueries({ queryKey: ['slots', slug] });
    } catch (e) {
      setCancelError(errorText(e));
    } finally {
      setCancelling(false);
    }
  };

  const share = async () => {
    const url = `${location.origin}/s/${slug}/my#t=${saved.token}`;
    try {
      if (navigator.share) await navigator.share({ title: `Запись в ${tenant.name}`, url });
      else {
        await navigator.clipboard.writeText(url);
        showToast({ body: 'Ссылка скопирована. Не пересылайте её посторонним — по ней можно отменить запись.' });
      }
    } catch {
      /* user cancelled share */
    }
  };

  return (
    <Card>
      <VStack gap={4}>
        <HStack hAlign="between" vAlign="start" gap={2}>
          <VStack gap={1}>
            <Heading level={3}>{b.service_name}</Heading>
            <Text>{longDateTime(b.starts_at, tenant.timezone)}</Text>
          </VStack>
          <Badge variant={st.variant} label={st.label} />
        </HStack>
        <MetadataList>
          <MetadataListItem label="Стоимость">{money(b.price_cents, tenant.currency, b.price_is_from)}</MetadataListItem>
          <MetadataListItem label="Адрес">{tenant.content.address}</MetadataListItem>
          {copy.hasCar && <MetadataListItem label="Автомобиль">{b.customer.car || '—'}</MetadataListItem>}
        </MetadataList>
        {b.status === 'confirmed' && <ReminderControls bookingId={b.id} token={saved.token} />}
        {b.status === 'confirmed' &&
          (b.can_change ? (
            <HStack gap={2} wrap="wrap">
              <Button label="Перенести" variant="secondary" onClick={() => setReschedule(true)} />
              <Button label="Отменить запись" variant="destructive" onClick={() => setConfirmCancel(true)} />
            </HStack>
          ) : (
            <Banner status="info" title="Изменить онлайн уже нельзя" description={`Перенос и отмена — до ${dateTime(b.change_deadline, tenant.timezone)}. Позвоните в ${copy.placeAcc}.`} collapsible={false} />
          ))}
        <HStack gap={2} wrap="wrap">
          <Button label="Позвонить" variant="ghost" icon={<Icon icon={Phone} />} href={`tel:${tenant.content.phone}`} />
          <Button label="Ссылка на запись" variant="ghost" icon={<Icon icon={LinkSimple} />} onClick={share} />
        </HStack>
        {cancelError && <Banner status="error" title={cancelError} collapsible={false} />}
      </VStack>
      <AlertDialog
        isOpen={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Отменить запись?"
        description={`${b.service_name}, ${dateTime(b.starts_at, tenant.timezone)}. Время освободится для других клиентов.`}
        actionLabel="Отменить запись"
        cancelLabel="Оставить"
        onAction={cancel}
        isActionLoading={cancelling}
      />
      <RescheduleSheet
        isOpen={reschedule}
        onOpenChange={setReschedule}
        booking={b}
        token={saved.token}
        onDone={(startsAt) => updateSavedBooking(slug, b.id, { startsAt })}
      />
    </Card>
  );
}
